import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import {
  venueConfigDeleteIconProps,
  venueConfigTableDataRowSx,
  venueConfigTableHeaderCellSx,
  venueConfigTableHeaderRowSx,
  venueConfigTableShellSx,
} from '../../venueConfigTableStyles';
import DateInput from 'components/DateInput/DateInput';
import DropDownInput from 'components/DropDownInput';
import InputField from 'components/InputField';
import { WeekDaysMultiSelect } from './WeekDaysMultiSelect';
import { ToolTip } from 'components/Tooltip/ToolTip';
import dayjs from 'dayjs';
import React, { FC, useState } from 'react';
import strings from '../../../../../../common/Translation/Translate';
import { VenueConfigEmptyState } from '../../VenueConfigEmptyState';
import { VenueConfigSectionAction } from '../../VenueConfigSectionAction';
import { identifiers } from '../../../../../../utils/constants/identifiers';
import {
  DropdownOption,
  fromDateInputValue,
  getPriceOverrideFieldErrors,
  PriceOverrideRow,
  priceDropDownDefaults,
  priceInlineFieldSx,
  toDateInputValue,
} from './priceFormUtils';

type PriceOverrideCardsProps = {
  rows: PriceOverrideRow[];
  packageOptions: DropdownOption[];
  weekDayOptions: DropdownOption[];
  errors?: Record<number, string>;
  onChange: (index: number, field: keyof PriceOverrideRow, value: any) => void;
  onPackageSelect: (index: number, option: DropdownOption | undefined) => void;
  onWeekDaysChange: (index: number, weekDays: DropdownOption[]) => void;
  onDelete: (index: number) => void;
  onAdd: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
};

const overrideRowSx = {
  ...venueConfigTableDataRowSx,
  flexWrap: 'nowrap' as const,
  minWidth: 0,
  gap: 1.5,
};

const today = dayjs();

export const PriceOverrideCards: FC<PriceOverrideCardsProps> = ({
  rows,
  packageOptions,
  weekDayOptions,
  errors = {},
  onChange,
  onPackageSelect,
  onWeekDaysChange,
  onDelete,
  onAdd,
  onReorder,
}) => {
  const dragDataKey = 'price-override-index';
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (toIndex: number, fromIndex: number) => {
    if (fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex);
    }
    handleDragEnd();
  };

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <VenueConfigSectionAction label={strings.venueConfig_price_addOverride} onClick={onAdd} />
        {rows.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {strings.venueConfig_price_dragReorderHint}
          </Typography>
        )}
      </Box>
      {rows.length === 0 ? (
        <VenueConfigEmptyState message={strings.venueConfig_price_overrides_empty} />
      ) : (
        <Box sx={{ ...venueConfigTableShellSx, overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1100 }}>
            <Box sx={{ ...venueConfigTableHeaderRowSx, gap: 1.5, alignItems: 'center' }}>
              <Box sx={{ width: 28, flexShrink: 0 }} />
              <Typography {...venueConfigTableHeaderCellSx} sx={{ width: 180, flexShrink: 0 }}>
                {strings.venueConfig_price_pricePackage}
              </Typography>
              <Typography {...venueConfigTableHeaderCellSx} sx={{ width: 120, flexShrink: 0 }}>
                {strings.venueConfig_price_pricePerPerson}
              </Typography>
              <Typography {...venueConfigTableHeaderCellSx} sx={{ width: 150, flexShrink: 0 }}>
                {strings.venueConfig_price_startDate}
              </Typography>
              <Typography {...venueConfigTableHeaderCellSx} sx={{ width: 150, flexShrink: 0 }}>
                {strings.venueConfig_price_endDate}
              </Typography>
              <Typography {...venueConfigTableHeaderCellSx} sx={{ flex: 1, minWidth: 200 }}>
                {strings.venueConfig_price_weekDays}
              </Typography>
              <Box sx={{ width: 40, flexShrink: 0 }} />
            </Box>
            {rows.map((row, index) => {
              const showFieldErrors = Boolean(errors[index]);
              const fieldErrors = showFieldErrors ? getPriceOverrideFieldErrors(row) : {};
              const isDragging = dragIndex === index;
              const isDragOver = dragOverIndex === index && dragIndex !== index;
              const endMinDate =
                row.startDate && toDateInputValue(row.startDate)
                  ? dayjs(toDateInputValue(row.startDate), identifiers.date_input)
                  : today;
              const packageInputValue = row.pricePackage?.value ?? row.pricePackageId ?? '';
              const matchedPackage = packageOptions.find(
                o => String(o.value) === String(packageInputValue),
              );
              const dropdownPackageValue = matchedPackage?.value ?? packageInputValue;

              return (
                <Box
                  key={row.id ?? `override-${index}`}
                  onDragOver={e => {
                    e.preventDefault();
                    setDragOverIndex(index);
                  }}
                  onDragLeave={() => {
                    if (dragOverIndex === index) setDragOverIndex(null);
                  }}
                  onDrop={e => {
                    e.preventDefault();
                    const fromIndex = Number(e.dataTransfer.getData(dragDataKey));
                    if (!Number.isNaN(fromIndex)) {
                      handleDrop(index, fromIndex);
                    }
                  }}
                  sx={{
                    ...overrideRowSx,
                    bgcolor: isDragOver ? 'action.hover' : undefined,
                    opacity: isDragging ? 0.5 : 1,
                    ...(isDragOver && { borderLeft: 2, borderLeftColor: 'primary.main' }),
                  }}>
                  <ToolTip title={strings.venueConfig_price_dragReorderHint}>
                    <Box
                      draggable
                      onDragStart={e => {
                        e.dataTransfer.setData(dragDataKey, String(index));
                        e.dataTransfer.effectAllowed = 'move';
                        setDragIndex(index);
                      }}
                      onDragEnd={handleDragEnd}
                      sx={{
                        width: 28,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.secondary',
                        cursor: 'grab',
                        pt: 1,
                      }}>
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                  </ToolTip>
                  <Box sx={{ ...priceInlineFieldSx, width: 180, flexShrink: 0 }}>
                    <DropDownInput
                      {...priceDropDownDefaults}
                      label={strings.venueConfig_price_pricePackage}
                      size="small"
                      field={`overridePackage-${index}`}
                      id={`overridePackage-${index}`}
                      inputValue={dropdownPackageValue}
                      options={packageOptions}
                      placeholder={strings.venueConfig_price_selectPackage}
                      hasError={Boolean(fieldErrors.pricePackage)}
                      fieldError={Boolean(fieldErrors.pricePackage)}
                      onChange={(value: string | number) => {
                        const opt = packageOptions.find(o => String(o.value) === String(value));
                        onPackageSelect(index, opt);
                      }}
                    />
                  </Box>
                  <Box sx={{ ...priceInlineFieldSx, width: 120, flexShrink: 0 }}>
                    <InputField
                      label={strings.venueConfig_price_pricePerPerson}
                      size="small"
                      typeValue="number"
                      field={`overridePpp-${index}`}
                      inputValue={row.pricePerPerson != null ? String(row.pricePerPerson) : ''}
                      hasError={Boolean(fieldErrors.pricePerPerson)}
                      fieldError={Boolean(fieldErrors.pricePerPerson)}
                      inputProps={{ min: 0, step: 'any' }}
                      textChange={value => onChange(index, 'pricePerPerson', value)}
                    />
                  </Box>
                  <Box sx={{ ...priceInlineFieldSx, width: 150, flexShrink: 0 }}>
                    <DateInput
                      field={`startDate-${index}`}
                      label={strings.venueConfig_price_startDate}
                      inputFormat={identifiers.date_format}
                      value={toDateInputValue(row.startDate ?? '')}
                      minDate={today}
                      size="small"
                      hasError={Boolean(fieldErrors.startDate)}
                      fieldError={Boolean(fieldErrors.startDate)}
                      textChange={val => onChange(index, 'startDate', fromDateInputValue(val))}
                      onChange={() => undefined}
                      renderInput={() => <TextField size="small" />}
                    />
                  </Box>
                  <Box sx={{ ...priceInlineFieldSx, width: 150, flexShrink: 0 }}>
                    <DateInput
                      field={`endDate-${index}`}
                      label={strings.venueConfig_price_endDate}
                      inputFormat={identifiers.date_format}
                      value={toDateInputValue(row.endDate ?? '')}
                      minDate={endMinDate}
                      size="small"
                      hasError={Boolean(fieldErrors.endDate)}
                      fieldError={Boolean(fieldErrors.endDate)}
                      textChange={val => onChange(index, 'endDate', fromDateInputValue(val))}
                      onChange={() => undefined}
                      renderInput={() => <TextField size="small" />}
                    />
                  </Box>
                  <Box sx={{ ...priceInlineFieldSx, flex: 1, minWidth: 200 }}>
                    <WeekDaysMultiSelect
                      id={`weekDays-${index}`}
                      label={strings.venueConfig_price_weekDays}
                      options={weekDayOptions}
                      value={row.weekDays ?? []}
                      hasError={Boolean(fieldErrors.weekDays)}
                      onChange={(selected: DropdownOption[]) => onWeekDaysChange(index, selected)}
                    />
                  </Box>
                  <ToolTip title={strings.deleteText}>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(index)}
                      aria-label="delete price override"
                      sx={{ mt: 0.5 }}>
                      <DeleteOutlineOutlinedIcon {...venueConfigDeleteIconProps} />
                    </IconButton>
                  </ToolTip>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};
